/* eslint-disable @typescript-eslint/ban-types */
import Cookies from 'js-cookie';
import * as CryptoJS from 'crypto-js';
import { marked } from 'marked';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { groupBy } from 'ramda';
import { LAB_TEST_STATUS } from 'context/visitContext';
import { LabTest, Patient, Prescription, Role } from '../types/types';
import readAWSS3File from './readAWSS3File';

export const AUTH_TOKEN_STORE_KEY = '_river_guide_tokid';

export const AUTH_USER_STORE_KEY = '_river_guide_usrid';

export const ASSIGNED_PATIENTS_STORE_KEY = '_river_assigned_patid';

export const USER_COMING_FROM_STORE_KEY = '_river_coming_from';

export const TEMP_ADVANCED_PATIENTS_SEARCHES_STORE_KEY = '_river_temp_patients_searches';

export const env = (value: string) => {
  const envVars = process.env;

  const key = `REACT_APP_${value}`;

  if (!(key in envVars)) {
    throw new Error(`no env config found for ${value}`);
  }

  return envVars[key];
};

export const getCookie = (key: string) => {
  const passPhrase = env('RIVER_PASSPHRASE') || '';

  const item = Cookies.get(key);

  if (!item) {
    return null;
  }

  const decipheredValue = CryptoJS.AES.decrypt(item, passPhrase);

  return decipheredValue.toString(CryptoJS.enc.Utf8);
};

export const setCookie = (key: string, value: string, expires = 7) => {
  const passPhrase = env('RIVER_PASSPHRASE') || '';

  const cipheredValue = CryptoJS.AES.encrypt(value, passPhrase).toString();

  if (env('RIVER_ENV') === 'development') {
    Cookies.set(key, cipheredValue);
    return;
  }

  if (env('RIVER_ENV') === 'staging') {
    Cookies.set(key, cipheredValue);
    return;
  }

  const options: Cookies.CookieAttributes = {
    expires,
    sameSite: 'Lax',
    secure: true,
  };

  Cookies.set(key, cipheredValue, options);
};

export const storage = {
  set: (key: string, value: string) => {
    const passPhrase = env('RIVER_PASSPHRASE') || '';
    const cipheredValue = CryptoJS.AES.encrypt(value, passPhrase).toString();
    localStorage.setItem(key, cipheredValue);
  },
  get: (key: string) => {
    const passPhrase = env('RIVER_PASSPHRASE') || '';
    const item = localStorage.getItem(key);

    if (!item) return null;

    try {
      const decipheredValue = CryptoJS.AES.decrypt(item, passPhrase);
      return decipheredValue.toString(CryptoJS.enc.Utf8);
    } catch (error) {
      console.log(`ERROR reading from storage with key: ${key}`, error);
      return '';
    }
  },
  remove: (key: string) => {
    localStorage.removeItem(key);
  },
};

export const sessionTStorage = {
  set: (key: string, value: string) => {
    const passPhrase = env('RIVER_PASSPHRASE') || '';
    const cipheredValue = CryptoJS.AES.encrypt(value, passPhrase).toString();
    window.sessionStorage.setItem(key, cipheredValue);
  },
  get: (key: string) => {
    const passPhrase = env('RIVER_PASSPHRASE') || '';
    const item = window.sessionStorage.getItem(key);

    if (!item) return null;

    try {
      const decipheredValue = CryptoJS.AES.decrypt(item, passPhrase);
      return decipheredValue.toString(CryptoJS.enc.Utf8);
    } catch (error) {
      console.log(`ERROR reading from session storage with key: ${key}`, error);
      return '';
    }
  },
  remove: (key: string) => {
    window.sessionStorage.removeItem(key);
  },
};

export function markdown(src: string, options?: marked.MarkedOptions, newTab = false) {
  const renderer = new marked.Renderer();
  renderer.link = function _link(href: string, title: string, text: string) {
    const link = marked.Renderer.prototype.link.apply(this, [href, title, text]);
    if (newTab) {
      return link.replace('<a', "<a target='_self'");
    }
    return link.replace('<a', "<a target='_blank'");
  };

  marked.setOptions({
    renderer,
  });

  return marked(src, options);
}

export const formatYupErrors = (error: any): Record<string, string> => {
  const objErrors: Record<string, string> = {};
  error.inner.forEach((fndError: any) => {
    objErrors[fndError.path] = fndError.message;
  });
  return objErrors;
};

export const callAllFns =
  (...fns: ((...args: any[]) => any)[]) =>
  (...args: any[]) => {
    return fns.forEach(fn => fn?.(...args));
  };

export const isValidURL = (url: string) => {
  const regex = /(http|https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-/]))?/;
  return regex.test(url);
};

export const capitalize = (value?: string) => {
  if (!value) return '';

  return value
    .toLowerCase()
    .split(' ')
    .map(word => {
      return word ? word.replace(word[0], word[0].toUpperCase()) : '';
    })
    .join(' ');
};

export function isFunction(value: any): value is Function {
  return (
    value &&
    ({}.toString.call(value) === '[object Function]' ||
      typeof value === 'function' ||
      value instanceof Function)
  );
}

export const moveFilteredItemsToTop = <T>({
  array,
  findFunction,
}: {
  array: Array<T>;
  findFunction: (item: T) => boolean;
}) => [...array.filter(findFunction), ...array.filter(item => !findFunction(item))];

export const formatPhoneNumber = (phoneNumberString: string) => {
  if (!phoneNumberString) {
    return phoneNumberString;
  }
  const phone = `${phoneNumberString}`.replace(/\D/g, '');
  const match = phone.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    const intlCode = match[1] ? '+1 ' : '';
    return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
  }
  return phoneNumberString;
};

export const calculateBMI = (heightInInches: number, weightInPounds: number) => {
  if (Number.isNaN(heightInInches) || Number.isNaN(weightInPounds)) {
    return undefined;
  }
  // Calculate BMI using the formula: weight / (height^2) * 703
  const bmi = (weightInPounds / heightInInches ** 2) * 703;
  return bmi.toFixed(2);
};

export const convertHeightToInches = (height: string) => {
  const [feet, inches] = (height.match(/\d+/g) || []).map(Number);
  return feet * 12 + inches;
};

export const inchesToFeetAndInches = (heightInInches: number) => {
  const feet = Math.floor(heightInInches / 12);
  const inches = heightInInches % 12;
  return { feet, inches };
};

export const randomTempIdGenerator = () => {
  return uuidv4();
};

export function isBoolean(value: any): value is boolean {
  return typeof value === 'boolean';
}

export function isNumber(value: any): value is number {
  return typeof value === 'number' && !Number.isNaN(value);
}

export function isString(value: any): value is string {
  return typeof value === 'string';
}

export const LANGUAGES = [
  {
    label: 'ENG',
    value: 'en',
  },
  {
    label: 'ESP',
    value: 'es',
  },
];

export const setLocale = (locale: string) => {
  sessionTStorage.set('locale', locale);
};

export const getLocale = () => {
  return sessionTStorage.get('locale');
};

export const isLocale = (locale: string) => {
  return sessionTStorage.get('locale') === locale;
};

export const isEnvironment = (systemEnv: string) => {
  return env('RIVER_ENV') === systemEnv;
};

export const isRole = (roles: Role[], role: Role) => {
  if (role === 'COLLABORATING-PHYSICIAN') {
    return roles.includes('COLLABORATING-PHYSICIAN') && !roles.includes('CARE-HEALTH-GUIDE');
  }
  return roles.includes(role);
};

export const isNativeApp = () => {
  return navigator.userAgent.indexOf('gonative') > -1;
};

export function truncateWithEllipses(text: string, limit: number) {
  return text.substring(0, limit - 1) + (text.length > limit ? '...' : '');
}

export const formatArrayIntoSentence = (array: string[]): string => {
  let outStr = '';
  if (array.length === 1) {
    [outStr] = array;
  } else if (array.length === 2) {
    outStr = array.join(' and ');
  } else if (array.length > 2) {
    outStr = `${array.slice(0, -1).join(', ')} and ${array.slice(-1)}`;
  }
  return outStr;
};

export const formatChatTime = (time: string) => {
  const currentTime = moment(Date.now()).format('MM/DD');
  const chatTime = moment(time).format('MM/DD');
  if (currentTime === chatTime) {
    return moment(time).format('h:mm a');
  }
  return moment(time).format('h:mm a');
  // return moment(time).format('MM/DD/YY • h:mm a');
};

export const calcReadableFileSize = (bytes: number) => {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === 0) return 'n/a';
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  if (i === 0) return `${bytes} ${sizes[i]})`;
  return `${(bytes / 1024 ** i).toFixed(1)} ${sizes[i]}`;
};

export const FILE_PATTERN_KEY = 'RIVER_ATTACHED_FILE';

export const FILE_PATTERN_KEY_REGEX = /<RIVER_ATTACHED_FILE:(.*?)>/;

export const ACCEPTED_CALL_PATTERN_KEY = 'RIVER_ACCEPTED_CALL';

export const ACCEPTED_CALL_PATTERN_KEY_REGEX = /<RIVER_ACCEPTED_CALL:(.*?)>/;

export const getFileExtension = (filename: string) =>
  filename.split('.').pop()?.toLocaleLowerCase() || '';

export const getFileType = (filenameOrUrl: string): 'image' | 'document' | 'unknown' => {
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'tiff', 'svg'];
  const documentExtensions = ['pdf', 'doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx', 'txt'];

  const extension = filenameOrUrl.split('.').pop()?.toLowerCase() || '';

  if (imageExtensions.includes(extension)) {
    return 'image';
  }

  if (documentExtensions.includes(extension)) {
    return 'document';
  }

  return 'unknown';
};

export const getFileNameFromURL = (url: string) => {
  if (!url) return url;

  const { pathname } = new URL(url);

  const lastSlashIndex = pathname.lastIndexOf('/');

  const fileNameWithExtension = pathname.substring(lastSlashIndex + 1);

  const lastDotIndex = fileNameWithExtension.lastIndexOf('.');

  const extension = fileNameWithExtension.substring(lastDotIndex + 1);

  const fileName = fileNameWithExtension.substring(0, lastDotIndex);

  return `${fileName}.${extension}`;
};

export const groupByDate = groupBy((data: Required<{ createdAt: string }>) => {
  return moment.utc(data.createdAt).local().calendar(null, {
    sameDay: '[Today]',
    lastDay: '[Yesterday]',
    lastWeek: 'dddd, DD MMM',
    sameElse: 'DD MMM, YYYY',
  });
});

export const hasOpenedFollowUp = (followUps?: any[] | null) => {
  return Boolean(
    followUps?.find(
      fndFollowUp => fndFollowUp.questionKey === 'STATUS' && fndFollowUp.answers.includes('OPENED'),
    ),
  );
};

export const hasOpenedLabReport = (labTests: LabTest[] | null) => {
  return !labTests?.some(labTest => {
    let hasLabURL = false;
    if (labTest.url) {
      const labURL = new URL(labTest.url);
      hasLabURL = Boolean(labTest.url && !labURL.searchParams.has('status'));
    }
    return labTest.status === LAB_TEST_STATUS.RECEIVED_REPORT && hasLabURL;
  });
};

export const parseQnAsTextContent = async (content: string) => {
  const match = content.match(FILE_PATTERN_KEY_REGEX);

  if (match) {
    const fileURL = match[1].trim();
    let textContent = content.replace(FILE_PATTERN_KEY_REGEX, '').trim();
    const fileURLs = fileURL.slice(1, -1).split(',');

    const readFileURLs = await Promise.all(
      fileURLs.map(async fndURL => readAWSS3File('chat', fndURL)),
    );

    textContent += ` <${FILE_PATTERN_KEY}:${`[${readFileURLs.join(',')}]`}>`;

    return textContent;
  }

  return content;
};

export const formatToFullAddress = (address: (string | undefined)[]) => {
  if (address.length === 0) {
    return 'No Location details found';
  }

  let streetAddress = address[0] || '';
  const apartment = address[1] || '';
  const city = address[2] || '';
  const state = address[3] || '';
  const zipCode = address[4] || '';

  streetAddress = streetAddress.replace(/\.$/, '');

  return `${streetAddress}${apartment ? ` ${apartment}` : ''}${
    streetAddress || apartment ? '.' : ''
  } ${city}${city && (state || zipCode) ? ',' : ''} ${state} ${zipCode}`;

  /* ['2772 Gaston Ave.', '20', 'Dallas', 'TX', '75201'] */
  /* Street Address Apartment. City, State ZipCode */
  /* 123 Sunnylane Ave. Los Angeles, LA 56902 */
};

export const parsePrescription = (data: any): Prescription => {
  return {
    id: data?.prescriptionPhotonId || data?.id,
    prescriptionPhotonId: data?.prescriptionPhotonId || '',
    medicationName: data?.medicationName || data?.treatment?.name || '',
    numberDispensed: data?.numberDispensed || data?.dispenseQuantity || 0,
    numberOfRefills: data?.numberOfRefills || (data?.fillsAllowed ? data.fillsAllowed - 1 : 0) || 0,
    doNotSubstitute: data?.doNotSubstitute || data?.dispenseAsWritten || false,
    numberToBeTaken: data?.numberToBeTaken || '',
    frequency: data?.frequency || '',
    dose: data?.dose || '',
    instructions: data?.instructions || '',
    daysSupply: data?.daysSupply || 0,
    dispenseUnit: data?.dispenseUnit || '',
    visitType: 'N/A',
    createdAt: data?.createdAt || data?.writtenAt || '',
  };
};

export const isAFamilyDependent = (patient: Patient) =>
  Boolean(
    patient &&
      patient?.familyManager &&
      patient?.familyManager?.subscriptionStatus === 'SUBSCRIBED',
  ); // When it has a family manager profile attached, then patient is a dependent

export const sleep = async (ms: number) => {
  // eslint-disable-next-line no-promise-executor-return
  return new Promise(resolve => setTimeout(resolve, ms));
};

export const updateFaviconWithCount = (count: number) => {
  const canvases = [];

  // Define the size of the canvas
  const size = 32;
  const badgeRadius = 10; // Increase the radius for a larger badge

  // Create a new canvas for each favicon
  const createCanvas = () => {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    canvases.push(canvas);
    return canvas;
  };

  const favicons = document.querySelectorAll<HTMLLinkElement>('#favicon');

  favicons.forEach(favicon => {
    const canvas = createCanvas();
    const context = canvas.getContext('2d');

    if (!context) return;

    if (count === 0) {
      favicon.setAttribute('href', '/favicon.png');
      return;
    }

    const baseImage = new Image();

    baseImage.src = favicon.href; // Use the current favicon source

    baseImage.onload = () => {
      context.drawImage(baseImage, 0, 0, size, size);

      // Draw a larger red circle for the badge
      context.fillStyle = '#F6914F';
      context.beginPath();
      context.arc(size - badgeRadius, badgeRadius, badgeRadius, 0, 2 * Math.PI);
      context.fill();

      // Draw the count number on the badge
      context.fillStyle = 'white';
      context.font = `bold ${badgeRadius * 1.5}px Arial`; // Bolder and larger font
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.fillText(count > 99 ? '99+' : `${count}`, size - badgeRadius, 11);

      // Convert the canvas to a data URL and set it as the favicon
      const newFavicon = canvas.toDataURL('image/png');
      favicon.setAttribute('href', newFavicon);
    };
  });
};

export const ABANDONED_VISIT_TRACKER = [
  {
    id: 'PATIENT-UNRESPONSIVE',
    abandonedReason: 'Patient unresponsive',
    abandonedReasonAdditionalDetails:
      'The patient did not respond to attempts to connect for the virtual visit.',
  },
  {
    id: 'NON-MEDICAL-VISIT',
    abandonedReason: 'Non-Medical Visit',
    abandonedReasonAdditionalDetails:
      'The virtual visit was not related to a medical issue and did not require a treatment plan.',
  },
];

export const GENDERS = [
  {
    value: 'MALE',
    label: 'Male',
  },
  {
    value: 'FEMALE',
    label: 'Female',
  },
  {
    value: 'TRANSGENDER',
    label: 'Transgender',
  },
  {
    value: 'NON-BINARY',
    label: 'Non-binary',
  },
];

export const US_STATES = [
  {
    value: 'AL',
    label: 'Alabama',
  },
  {
    value: 'AK',
    label: 'Alaska',
  },
  {
    value: 'AZ',
    label: 'Arizona',
  },
  {
    value: 'AR',
    label: 'Arkansas',
  },
  {
    value: 'CA',
    label: 'California',
  },
  {
    value: 'CO',
    label: 'Colorado',
  },
  {
    value: 'CT',
    label: 'Connecticut',
  },
  {
    value: 'DE',
    label: 'Delaware',
  },
  {
    value: 'FL',
    label: 'Florida',
  },
  {
    value: 'GA',
    label: 'Georgia',
  },
  {
    value: 'HI',
    label: 'Hawaii',
  },
  {
    value: 'ID',
    label: 'Idaho',
  },
  {
    value: 'IL',
    label: 'Illinois',
  },
  {
    value: 'IN',
    label: 'Indiana',
  },
  {
    value: 'IA',
    label: 'Iowa',
  },
  {
    value: 'KS',
    label: 'Kansas',
  },
  {
    value: 'KY',
    label: 'Kentucky',
  },
  {
    value: 'LA',
    label: 'Louisiana',
  },
  {
    value: 'ME',
    label: 'Maine',
  },
  {
    value: 'MD',
    label: 'Maryland',
  },
  {
    value: 'MA',
    label: 'Massachusetts',
  },
  {
    value: 'MI',
    label: 'Michigan',
  },
  {
    value: 'MN',
    label: 'Minnesota',
  },
  {
    value: 'MS',
    label: 'Mississippi',
  },
  {
    value: 'MO',
    label: 'Missouri',
  },
  {
    value: 'MT',
    label: 'Montana',
  },
  {
    value: 'NC',
    label: 'North Carolina',
  },
  {
    value: 'ND',
    label: 'North Dakota',
  },
  {
    value: 'NE',
    label: 'Nebraska',
  },
  {
    value: 'NV',
    label: 'Nevada',
  },
  {
    value: 'NH',
    label: 'New Hampshire',
  },
  {
    value: 'NJ',
    label: 'New Jersey',
  },
  {
    value: 'NM',
    label: 'New Mexico',
  },
  {
    value: 'NY',
    label: 'New York',
  },
  {
    value: 'OH',
    label: 'Ohio',
  },
  {
    value: 'OK',
    label: 'Oklahoma',
  },
  {
    value: 'OR',
    label: 'Oregon',
  },
  {
    value: 'PA',
    label: 'Pennsylvania',
  },
  {
    value: 'PR',
    label: 'Puerto Rico',
  },
  {
    value: 'RI',
    label: 'Rhode Island',
  },
  {
    value: 'SC',
    label: 'South Carolina',
  },
  {
    value: 'SD',
    label: 'South Dakota',
  },
  {
    value: 'TN',
    label: 'Tennessee',
  },
  {
    value: 'TX',
    label: 'Texas',
  },
  {
    value: 'UT',
    label: 'Utah',
  },
  {
    value: 'VT',
    label: 'Vermont',
  },
  {
    value: 'VA',
    label: 'Virginia',
  },
  {
    value: 'DC',
    label: 'Washington DC',
  },
  {
    value: 'WA',
    label: 'Washington',
  },
  {
    value: 'WV',
    label: 'West Virginia',
  },
  {
    value: 'WI',
    label: 'Wisconsin',
  },
  {
    value: 'WY',
    label: 'Wyoming',
  },
];
